import { useLayoutEffect, useRef } from 'react';

/**
 * Make a game screen fit the phone, exactly once, without scrolling.
 *
 * The games are the only screens in the app where scrolling costs something:
 * the thing worth seeing is an animation on a canvas, and a player who has to
 * scroll to reach the button has already missed it. Everywhere else in the app
 * a long page is fine.
 *
 * ── What has to be measured, and why it cannot be guessed ────────────────────
 *
 * A height in `vh` is wrong because the app puts a header above the screen and
 * a fixed tab bar below it, and the hymn dock floats above that whenever a
 * hymn is playing. Those change with the viewport, with the route, and with
 * whether the player has music on. So the room is measured: from the top of
 * the section down to whichever comes first of the tab bar, the dock, or the
 * bottom of the window.
 *
 * ── The correction, and why it has to be sticky ──────────────────────────────
 *
 * The app also keeps a fixed pad below the last screen to clear its own tab
 * bar, and that pad sits outside everything measured above — so filling the
 * measured room exactly still leaves the page a few pixels long.
 *
 * The surplus is given back through a trim that only ever grows, and is reset
 * when the viewport itself changes. Recomputing it from the current overflow
 * on each pass does not work: the correction removes the overflow, the next
 * pass therefore sees none, the height springs back, and the layout
 * oscillates between fitting and not.
 *
 * The trim is also kept per mode. Sharing one between a playing screen and a
 * results screen charges the results panel's content overflow to the canvas,
 * and the canvas comes out a third of the size it had room for.
 *
 * @param ref    the section to size
 * @param mode   a key for the current layout; each gets its own trim
 * @param apply  called with the room available, in CSS pixels
 * @param deps   anything else that should force a re-measure
 */
export default function useFillViewport(ref, mode, apply, deps = []) {
  const trims = useRef({});
  const viewport = useRef('');
  const applyRef = useRef(apply);
  applyRef.current = apply;

  useLayoutEffect(() => {
    const section = ref.current;
    if (!section) return undefined;

    const measure = () => {
      // Converted to document space with the same scroll offset throughout, so
      // the result is a true distance whatever the page happens to be doing.
      const top = section.getBoundingClientRect().top + window.scrollY;
      /*
       * Only things actually on screen can be in the way. A `display: none`
       * element still answers getBoundingClientRect — with zeroes — so an
       * immersive screen that hides the tab bar would otherwise measure its
       * floor at y=0 and give up.
       */
      const floorOf = (selector) => {
        const el = document.querySelector(selector);
        if (!el) return Infinity;
        const r = el.getBoundingClientRect();
        if (r.width === 0 && r.height === 0) return Infinity;
        return r.top + window.scrollY;
      };
      const edge = Math.min(
        floorOf('.nav'),
        floorOf('.dock'),
        window.scrollY + window.innerHeight,
      );
      // No slack: on an immersive screen a margin here shows as a seam of app
      // background along the bottom edge. Genuine overshoot is handled by the
      // trim below, which is measured rather than guessed at.
      const avail = edge - top;
      if (!Number.isFinite(avail) || avail < 80) return;

      const key = `${window.innerWidth}x${window.innerHeight}`;
      if (viewport.current !== key) {
        viewport.current = key;
        trims.current = {};
      }

      /*
       * Size first, then look for overflow.
       *
       * Measuring before applying charges the trim for the un-sized first
       * paint — a screen that has not been laid out yet can be hundreds of
       * pixels long — and the correction then eats space the layout was
       * entitled to. Once the layout is applied, whatever is still over is the
       * app's own pad, which is what this is for.
       */
      applyRef.current(avail - (trims.current[mode] ?? 0));

      const doc = document.documentElement;
      const overflow = Math.max(0, doc.scrollHeight - doc.clientHeight);
      // Capped: a large overflow means something else is wrong, and eating the
      // canvas to hide it would be the wrong fix.
      if (overflow > 1) {
        trims.current[mode] = Math.min((trims.current[mode] ?? 0) + overflow, 80);
        applyRef.current(avail - trims.current[mode]);
      }
    };

    measure();
    // The dock comes and goes with the hymn player, and panels change height
    // between phases; both move the floor under this.
    const ro = new ResizeObserver(measure);
    ro.observe(document.body);
    window.addEventListener('resize', measure);
    window.addEventListener('orientationchange', measure);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', measure);
      window.removeEventListener('orientationchange', measure);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref, mode, ...deps]);
}
